/**
 * Moderation data layer (ALP-65/72): flags & tags, archive/pin, commenter marks and enrichment.
 * Optimistic where it's cheap (flags, archive, pin, mark) and always reconciled with the server.
 */
import { BUILT_IN_FLAGS } from "@malfak/contracts";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { toast } from "sonner";
import { announceJob } from "@/features/jobs";
import { useMe, useOrg } from "@/hooks/data";
import { api } from "@/lib/api/client";
import { isApiError } from "@/lib/api/errors";
import { useLiveEvent } from "@/lib/api/events";
import { useApiQuery } from "@/lib/api/hooks";
import { useT } from "@/lib/i18n";
import type { Comment, CommentDetail, CommenterMark, FlaggableEntity, InboxItem, Job, Page, Tag } from "@/lib/types";

export const BUILT_INS: readonly string[] = BUILT_IN_FLAGS;
export const isBuiltIn = (key: string) => BUILT_INS.includes(key);

export function errorText(e: unknown): string | undefined {
  return isApiError(e) ? (e.problem?.detail ?? e.problem?.title) : undefined;
}

/** Built-in flags + custom tags, plus a key → Tag lookup. */
export function useTags() {
  const q = useApiQuery("tags", {}, { staleTime: 60_000 });
  const byKey = useMemo(() => new Map((q.data ?? []).map((t) => [t.key, t])), [q.data]);
  const custom = useMemo(() => (q.data ?? []).filter((t) => !t.builtIn), [q.data]);
  return { ...q, tags: q.data ?? [], custom, byKey };
}

/** Role of the signed-in user in the current org (null while loading). */
export function useRole() {
  const org = useOrg();
  useMe();
  return org?.role ?? null;
}
export const canWrite = (role: string | null) => role === "owner" || role === "admin" || role === "editor";
export const isAdmin = (role: string | null) => role === "owner" || role === "admin";

/** Localized label of a flag key (built-in → translation, custom → its label, unknown → the key). */
export function useFlagLabel() {
  const { t } = useT();
  const { byKey } = useTags();
  return (key: string) => (isBuiltIn(key) ? t(`moderation.flags.${key as "spam"}`) : (byKey.get(key)?.label ?? key));
}

// ------------------------------------------------------------------ cache patching (inbox, post comments, quick panel)
type InboxPages = InfiniteData<Page<InboxItem>, string | undefined>;

/** Patch comments wherever they are cached; returns a rollback. */
export function patchComments(queryClient: QueryClient, ids: string[], patch: (c: Comment) => Partial<Comment>): () => void {
  const set = new Set(ids);
  const snaps = [
    ...queryClient.getQueriesData({ queryKey: ["inbox"] }),
    ...queryClient.getQueriesData({ queryKey: ["comments"] }),
    ...queryClient.getQueriesData({ queryKey: ["commentDetail"] }),
  ];
  const fix = (c: Comment) => (set.has(c.id) ? { ...c, ...patch(c) } : c);
  const fixItems = (items: InboxItem[]) => items.map((it) => (it.type === "comment" && set.has(it.comment.id) ? { ...it, comment: fix(it.comment) } : it));
  queryClient.setQueriesData<InboxPages | Page<InboxItem>>({ queryKey: ["inbox"] }, (old) => {
    if (!old) return old;
    if ("pages" in old) return { ...old, pages: old.pages.map((p) => ({ ...p, items: fixItems(p.items) })) };
    return { ...old, items: fixItems(old.items) };
  });
  queryClient.setQueriesData<Page<Comment> | InfiniteData<Page<Comment>>>({ queryKey: ["comments"] }, (old) => {
    if (!old) return old;
    if ("pages" in old) return { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map(fix) })) };
    return { ...old, items: old.items.map(fix) };
  });
  queryClient.setQueriesData<CommentDetail>({ queryKey: ["commentDetail"] }, (old) => (old ? { ...old, comment: fix(old.comment), thread: old.thread.map(fix) } : old));
  return () => snaps.forEach(([k, d]) => queryClient.setQueryData(k, d));
}

/** Patch conversations' flags in the inbox caches. */
function patchConversationFlags(queryClient: QueryClient, ids: string[], next: (flags: string[]) => string[]) {
  const set = new Set(ids);
  const fixItems = (items: InboxItem[]) =>
    items.map((it) => (it.type === "conversation" && set.has(it.conversation.id) ? { ...it, conversation: { ...it.conversation, flags: next(it.conversation.flags ?? []) } } : it));
  queryClient.setQueriesData<InboxPages | Page<InboxItem>>({ queryKey: ["inbox"] }, (old) => {
    if (!old) return old;
    if ("pages" in old) return { ...old, pages: old.pages.map((p) => ({ ...p, items: fixItems(p.items) })) };
    return { ...old, items: fixItems(old.items) };
  });
}

/** Invalidate route caches on the given client. */
export const refresh = (qc: QueryClient, ...keys: string[]) => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: [k] })));

const applyFlags = (flags: string[], add: string[], remove: string[]) => [...new Set([...flags.filter((f) => !remove.includes(f)), ...add])].sort();

// ------------------------------------------------------------------ mutations
export function useSetFlags() {
  const { t } = useT();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { entityType: FlaggableEntity; ids: string[]; add?: string[]; remove?: string[] }) => api("setFlags", { body: { entityType: v.entityType, ids: v.ids, add: v.add ?? [], remove: v.remove ?? [] } }),
    onMutate: (v) => {
      const add = v.add ?? [], remove = v.remove ?? [];
      if (v.entityType === "comment") return { rollback: patchComments(qc, v.ids, (c) => ({ flags: applyFlags(c.flags ?? [], add, remove) })) };
      if (v.entityType === "conversation") {
        const snap = qc.getQueriesData({ queryKey: ["inbox"] });
        patchConversationFlags(qc, v.ids, (f) => applyFlags(f, add, remove));
        return { rollback: () => snap.forEach(([k, d]) => qc.setQueryData(k, d)) };
      }
      return { rollback: () => {} };
    },
    onSuccess: (r, v) => {
      if (v.ids.length > 1) toast.success(t("moderation.actions.flagged"), { duration: 1800 });
      if (v.entityType === "contact") void refresh(qc, "contact", "contacts", "commenters", "commentDetail", "contact360", "contactTimeline", "contactSummary");
      void refresh(qc, "flagCounts");
      return r;
    },
    onError: (e, _v, ctx) => {
      ctx?.rollback();
      toast.error(t("moderation.flagMenu.failed"), { description: errorText(e) });
    },
  });
}

/** Archive/unarchive and pin/unpin (Malfak-side states). */
export function useCommentState() {
  const { t } = useT();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (v: { id: string; archived?: boolean; pinned?: boolean }) => api("updateComment", { params: { commentId: v.id }, body: { archived: v.archived, pinned: v.pinned } }),
    onMutate: (v) => {
      const at = new Date().toISOString();
      return {
        rollback: patchComments(qc, [v.id], () => ({
          ...(v.archived !== undefined ? { archivedAt: v.archived ? at : null } : {}),
          ...(v.pinned !== undefined ? { pinnedAt: v.pinned ? at : null } : {}),
        })),
      };
    },
    onSuccess: (_c, v) => {
      const key = v.archived !== undefined ? (v.archived ? "archived" : "unarchived") : v.pinned ? "pinned" : "unpinned";
      toast.success(t(`moderation.actions.${key}`), {
        duration: 3500,
        action: { label: t("common.actions.undo"), onClick: () => m.mutate({ id: v.id, ...(v.archived !== undefined ? { archived: !v.archived } : { pinned: !v.pinned }) }) },
      });
      void refresh(qc, "comments", "inbox", "inboxCounts", "flagCounts");
    },
    onError: (e, _v, ctx) => {
      ctx?.rollback();
      toast.error(t("moderation.actions.failed"), { description: errorText(e) });
    },
  });
  return m;
}

export function useMarkContact() {
  const { t } = useT();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { contactId: string; mark: CommenterMark | null }) => api("markContact", { params: { contactId: v.contactId }, body: { mark: v.mark } }),
    onSuccess: (_c, v) => {
      toast.success(v.mark ? t("moderation.marks.marked", { mark: t(`moderation.marks.${v.mark}`) }) : t("moderation.marks.cleared"), { duration: 2000 });
      void refresh(qc, "contact", "contacts", "commenters", "commentDetail", "contact360", "contactTimeline", "contactSummary");
    },
    onError: (e) => toast.error(t("moderation.actions.failed"), { description: errorText(e) }),
  });
}

/** Run something once when a job reaches a terminal state (SSE `operation.updated`). */
export function useJobFinished(jobId: string | null | undefined, onFinish: (status: string) => void) {
  const cb = useRef(onFinish);
  cb.current = onFinish;
  useLiveEvent(
    (e) => {
      if (!jobId || e.type !== "operation.updated" || e.operationId !== jobId) return;
      if (e.status === "succeeded" || e.status === "failed" || e.status === "cancelled") cb.current(e.status);
    },
    [jobId],
  );
}

export function useEnrichContact() {
  const { t } = useT();
  return useMutation({
    mutationFn: (contactId: string) => api("enrichContact", { params: { contactId }, body: {} }),
    onSuccess: (job: Job) => announceJob(job),
    onError: (e) => toast.error(t("moderation.actions.failed"), { description: errorText(e) }),
  });
}

export function useContactCommentsAction() {
  const { t } = useT();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { contactId: string; action: "archive" | "unarchive" | "hide" | "unhide"; accountId?: string }) => api("contactCommentsAction", { params: { contactId: v.contactId }, body: { action: v.action, accountId: v.accountId } }),
    onSuccess: (job: Job, v) => {
      announceJob(job);
      if (v.action === "archive" || v.action === "unarchive") void refresh(qc, "comments", "inbox", "inboxCounts", "commenters", "commentDetail");
    },
    onError: (e) => toast.error(t("moderation.actions.failed"), { description: errorText(e) }),
  });
}

/** Bulk routes answer with a BatchJob id: load the Job for the tray/toasts; fall back when the jobs center can't resolve it. */
export async function announceById(id: string, fallback: () => void) {
  try {
    announceJob(await api("job", { params: { jobId: id } }));
  } catch {
    fallback();
  }
}

/** Sorted flag keys for display: built-ins first (in their canonical order), then custom tags by label. */
export function sortFlags(keys: string[], byKey: Map<string, Tag>): string[] {
  return [...keys].sort((a, b) => {
    const ia = BUILT_INS.indexOf(a), ib = BUILT_INS.indexOf(b);
    if (ia >= 0 || ib >= 0) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    return (byKey.get(a)?.label ?? a).localeCompare(byKey.get(b)?.label ?? b);
  });
}
